/**
 * Leaf discovery (plan decision D7).
 *
 * The pool emits every insertion as an event through its event authority, so
 * a tree's leaves are the events of the transactions that touched its PDA,
 * in slot order. The tree is rebuilt from those events and accepted only
 * when its root and leaf count equal the tree account read at a finalized
 * slot at or after the last transaction folded in. Nothing here trusts the
 * RPC: a gap, a duplicate, a stale cache or an inconsistent view all end in
 * a rescan or a refusal, never in a wrong witness.
 */
import {
  decodeSolanaTree,
  extractSolanaPoolEvents,
  getSolanaAccounts,
  solanaPoolAddresses,
  solanaPoolEventLeaves,
  readSolanaCommitmentRecords,
  type SolanaTree,
} from "@stealthos/sdk";
import { FlatJoinSplitV2MerkleTree } from "../merkleIndex";
import {
  assertNotAborted,
  fail,
  requireSafeIndex,
  sleep,
  solanaTreeCurrentRoot,
} from "./common";
import {
  createMemorySolanaLeafCache,
  solanaLeafCacheKey,
  type SolanaLeafCache,
} from "./leafCache";
import {
  getSolanaSignaturesForAddress,
  getSolanaTransaction,
  type SolanaRpc,
  type SolanaSignatureInfo,
} from "./rpc";

export interface SolanaTreeScanNetwork {
  key: string;
  programId: string;
  deploymentSlot: number;
  onchainRecoveryEnabled?: boolean;
}

export interface SolanaTreeScan {
  treeIndex: number;
  tree: FlatJoinSplitV2MerkleTree;
  root: string;
  /** Finalized slot of the tree account this scan was verified against. */
  slot: number;
  account: SolanaTree;
  reusedLeaves: number;
  fetchedTransactions: number;
}

const MAX_ATTEMPTS = 3;
const MAX_TRANSACTIONS_PER_SCAN = 20_000;
const sessionCache = createMemorySolanaLeafCache();

async function readTreeAccount(
  rpc: SolanaRpc,
  programId: string,
  treeIndex: number,
): Promise<{ slot: number; account: SolanaTree }> {
  const addresses = solanaPoolAddresses(programId);
  const { slot, accounts } = await getSolanaAccounts(
    rpc,
    [addresses.tree(treeIndex)],
    "finalized",
  );
  const info = accounts[0];
  if (!info) fail(`tree ${treeIndex} does not exist on this pool`);
  if (info.owner !== addresses.programId) {
    fail(`tree ${treeIndex} account is not owned by the pool program`);
  }
  const account = decodeSolanaTree(info.data);
  if (account.index !== treeIndex) fail("tree account index mismatch");
  return { slot, account };
}

/**
 * Finalized, successful pool transactions on the tree PDA newer than `until`
 * and not newer than `maxSlot`, oldest first.
 */
async function collectSignatures(
  rpc: SolanaRpc,
  treeAddress: string,
  options: {
    until?: string;
    maxSlot: number;
    minSlot: number;
    pageSize: number;
    signal?: AbortSignal;
  },
): Promise<SolanaSignatureInfo[]> {
  const collected: SolanaSignatureInfo[] = [];
  let before: string | undefined;
  while (true) {
    assertNotAborted(options.signal, "during leaf discovery");
    const page = await getSolanaSignaturesForAddress(rpc, treeAddress, {
      before,
      until: options.until,
      limit: options.pageSize,
      commitment: "finalized",
    });
    for (const info of page) {
      if (info.slot >= options.minSlot && info.slot <= options.maxSlot) {
        collected.push(info);
      }
    }
    if (collected.length > MAX_TRANSACTIONS_PER_SCAN) {
      fail(
        "tree history exceeds what the browser rebuilds from transactions; an indexer is required for this pool",
      );
    }
    const oldest = page[page.length - 1];
    if (
      page.length < options.pageSize ||
      !oldest ||
      oldest.slot < options.minSlot
    ) {
      break;
    }
    before = oldest.signature;
  }
  return collected.reverse();
}

export async function scanSolanaTree(params: {
  rpc: SolanaRpc;
  network: SolanaTreeScanNetwork;
  treeIndex: number;
  cache?: SolanaLeafCache;
  signal?: AbortSignal;
  pageSize?: number;
  retryDelayMs?: number;
  /** Cold recovery requires durable leaves. Historical events are migration-only. */
  requireDurableLeaves?: boolean;
}): Promise<SolanaTreeScan> {
  const { rpc, network } = params;
  const treeIndex = requireSafeIndex(params.treeIndex, "treeIndex");
  const cache = params.cache ?? sessionCache;
  const pageSize = params.pageSize ?? 1000;
  const addresses = solanaPoolAddresses(network.programId);
  const treeAddress = addresses.tree(treeIndex);
  const cacheKey = solanaLeafCacheKey(
    network.key,
    addresses.programId,
    treeIndex,
  );
  let cached = await cache.load(cacheKey);

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    assertNotAborted(params.signal, "before leaf discovery");
    const { slot, account } = await readTreeAccount(
      rpc,
      addresses.programId,
      treeIndex,
    );
    const expectedLeafCount = requireSafeIndex(
      account.nextLeafIndex,
      "nextLeafIndex",
    );
    const expectedRoot = solanaTreeCurrentRoot(account);

    if (cached && cached.leaves.length > expectedLeafCount) {
      // A finalized tree never shrinks; the cache cannot be this pool's.
      cached = null;
      await cache.clear(cacheKey);
    }
    if (cached && cached.leaves.length === expectedLeafCount) {
      const tree = await FlatJoinSplitV2MerkleTree.create(
        treeIndex,
        cached.leaves,
      );
      if (tree.root === expectedRoot && cached.root === expectedRoot) {
        return {
          treeIndex,
          tree,
          root: expectedRoot,
          slot,
          account,
          reusedLeaves: cached.leaves.length,
          fetchedTransactions: 0,
        };
      }
      cached = null;
      await cache.clear(cacheKey);
    }

    if (network.onchainRecoveryEnabled || params.requireDurableLeaves) {
      const durable = await readSolanaCommitmentRecords(
        rpc,
        addresses.programId,
        treeIndex,
        slot,
      );
      const durableLeaves: Array<string | undefined> = new Array(
        expectedLeafCount,
      );
      let legacyCount = 0;
      for (const { record } of durable.records) {
        // A later finalized scan can see leaves appended after this tree snapshot.
        if (record.slot > BigInt(slot)) continue;
        if (record.leafIndex >= expectedLeafCount)
          fail("durable leaf record exceeds the finalized tree");
        if (!record.commitment) {
          legacyCount += 1;
          continue;
        }
        if (durableLeaves[record.leafIndex] !== undefined)
          fail("duplicate durable leaf index");
        durableLeaves[record.leafIndex] = record.commitment;
      }
      if (durableLeaves.filter(Boolean).length === expectedLeafCount) {
        const leaves = durableLeaves as string[];
        const tree = await FlatJoinSplitV2MerkleTree.create(treeIndex, leaves);
        if (tree.root !== expectedRoot)
          fail("durable leaf records do not match the finalized tree root");
        await cache.save(cacheKey, {
          schemaVersion: 1,
          leaves,
          newestSignature: null,
          newestSlot: slot,
          root: expectedRoot,
        });
        return {
          treeIndex,
          tree,
          root: expectedRoot,
          slot,
          account,
          reusedLeaves: 0,
          fetchedTransactions: 0,
        };
      }
      if (params.requireDurableLeaves || legacyCount === 0) {
        fail(
          `tree ${treeIndex} is missing durable leaves; legacy leaf hydration must finish before independent recovery`,
        );
      }
    }
    // Only legacy records can use the historical migration path. A partial
    // modern scan is an error rather than an apparently complete balance.
    const signatures = await collectSignatures(rpc, treeAddress, {
      until: cached?.newestSignature ?? undefined,
      maxSlot: slot,
      minSlot: network.deploymentSlot,
      pageSize,
      signal: params.signal,
    });
    const leaves = cached ? [...cached.leaves] : [];
    const reusedLeaves = leaves.length;
    let newest = cached
      ? { signature: cached.newestSignature, slot: cached.newestSlot }
      : null;
    for (const info of signatures) {
      assertNotAborted(params.signal, "during leaf discovery");
      newest = { signature: info.signature, slot: info.slot };
      if (info.err !== null) continue;
      const transaction = await getSolanaTransaction(
        rpc,
        info.signature,
        "finalized",
      );
      if (!transaction) {
        fail(
          `finalized transaction ${info.signature} is no longer available from the RPC; the node's history does not cover this pool`,
        );
      }
      for (const { event } of extractSolanaPoolEvents(
        transaction,
        addresses.programId,
      )) {
        for (const leaf of solanaPoolEventLeaves(event)) {
          if (leaf.treeIndex !== treeIndex) continue;
          const commitment = leaf.commitment.toLowerCase();
          if (leaf.leafIndex < leaves.length) {
            // Overlap with what the cache already holds (the `until` anchor
            // was not honoured); it must agree exactly.
            if (leaves[leaf.leafIndex] !== commitment) {
              fail(
                `leaf ${leaf.leafIndex} of tree ${treeIndex} disagrees with the cached tree; clearing the cache`,
              );
            }
            continue;
          }
          if (leaf.leafIndex !== leaves.length) {
            fail(
              `leaf discovery gap in tree ${treeIndex}: expected leaf ${leaves.length}, saw ${leaf.leafIndex} in ${info.signature}`,
            );
          }
          leaves.push(commitment);
        }
      }
    }

    if (leaves.length === expectedLeafCount) {
      const tree = await FlatJoinSplitV2MerkleTree.create(treeIndex, leaves);
      if (tree.root !== expectedRoot) {
        if (cached) {
          cached = null;
          await cache.clear(cacheKey);
          continue;
        }
        fail(
          `rebuilt tree ${treeIndex} root ${tree.root} does not match the tree account root ${expectedRoot}`,
        );
      }
      await cache.save(cacheKey, {
        schemaVersion: 1,
        leaves,
        newestSignature: newest?.signature ?? null,
        newestSlot: newest?.slot ?? 0,
        root: expectedRoot,
      });
      return {
        treeIndex,
        tree,
        root: expectedRoot,
        slot,
        account,
        reusedLeaves,
        fetchedTransactions: signatures.length,
      };
    }
    // The transaction view and the account view disagree (an RPC node behind
    // on one of the two indexes). Start over; a cache that outran the account
    // cannot be trusted either.
    if (leaves.length > expectedLeafCount && cached) {
      cached = null;
      await cache.clear(cacheKey);
    }
    if (attempt < MAX_ATTEMPTS) {
      await sleep(params.retryDelayMs ?? 1_000, params.signal);
    }
  }
  return fail(
    `tree ${treeIndex} transactions and account state did not agree after ${MAX_ATTEMPTS} attempts`,
  );
}
